/**
 * Freehand Drawing Tool - Smoothing Pipeline
 * Implements Ramer-Douglas-Peucker simplification and cubic Bezier curve fitting
 */

import { vec2 } from './geometry';

// ============================================================================
// Point Sampling and Filtering
// ============================================================================

export function samplePoints(rawPoints, minDistance = 2, minTime = 8) {
  if (rawPoints.length <= 1) return rawPoints;
  
  const sampled = [rawPoints[0]];
  let lastSampleTime = 0;
  
  for (let i = 1; i < rawPoints.length; i++) {
    const currentTime = i * minTime; // simplified time model
    const point = rawPoints[i];
    const lastPoint = sampled[sampled.length - 1];
    
    const distance = vec2.distance(point, lastPoint);
    const timeDelta = currentTime - lastSampleTime;
    
    if (distance >= minDistance && timeDelta >= minTime) {
      sampled.push(point);
      lastSampleTime = currentTime;
    }
  }
  
  // Always include the last point
  if (!vec2.equals(sampled[sampled.length - 1], rawPoints[rawPoints.length - 1])) {
    sampled.push(rawPoints[rawPoints.length - 1]);
  }
  
  return sampled;
}

export function applyLowPassFilter(points, alpha = 0.3) {
  if (points.length <= 1) return points;
  
  const filtered = [points[0]];
  
  for (let i = 1; i < points.length; i++) {
    const prev = filtered[i - 1];
    const curr = points[i];
    filtered.push(vec2.lerp(prev, curr, alpha));
  }
  
  return filtered;
}

// ============================================================================
// Ramer-Douglas-Peucker Simplification
// ============================================================================

function perpendicularDistance(point, lineStart, lineEnd) {
  const line = vec2.subtract(lineEnd, lineStart);
  const lineLength = vec2.length(line);
  
  if (lineLength === 0) {
    return vec2.distance(point, lineStart);
  }
  
  const t = Math.max(0, Math.min(1, vec2.dot(vec2.subtract(point, lineStart), line) / (lineLength * lineLength)));
  const projection = vec2.add(lineStart, vec2.multiply(line, t));
  
  return vec2.distance(point, projection);
}

function rdpRecursive(points, start, end, epsilon, result) {
  if (end - start <= 1) return;
  
  let maxDistance = 0;
  let maxIndex = start;
  
  for (let i = start + 1; i < end; i++) {
    const distance = perpendicularDistance(points[i], points[start], points[end]);
    if (distance > maxDistance) {
      maxDistance = distance;
      maxIndex = i;
    }
  }
  
  if (maxDistance > epsilon) {
    result[maxIndex] = true;
    rdpRecursive(points, start, maxIndex, epsilon, result);
    rdpRecursive(points, maxIndex, end, epsilon, result);
  }
}

export function rdpSimplify(points, epsilon = 1.0) {
  if (points.length <= 2) return points;
  
  const keep = new Array(points.length).fill(false);
  keep[0] = true;
  keep[points.length - 1] = true;
  
  rdpRecursive(points, 0, points.length - 1, epsilon, keep);
  
  return points.filter((_, index) => keep[index]);
}

// ============================================================================
// Cubic Bezier Curve Fitting (Schneider's Algorithm)
// ============================================================================

function chordLengthParameterize(points) {
  const params = [0];
  let totalLength = 0;
  
  for (let i = 1; i < points.length; i++) {
    totalLength += vec2.distance(points[i - 1], points[i]);
    params.push(totalLength);
  }
  
  // Normalize to [0, 1]
  return params.map(p => totalLength > 0 ? p / totalLength : 0);
}

function computeTangent(points, index) {
  if (index === 0) {
    return vec2.normalize(vec2.subtract(points[1], points[0]));
  } else if (index === points.length - 1) {
    return vec2.normalize(vec2.subtract(points[index], points[index - 1]));
  } else {
    const prev = vec2.subtract(points[index], points[index - 1]);
    const next = vec2.subtract(points[index + 1], points[index]);
    return vec2.normalize(vec2.add(prev, next));
  }
}

function generateBezier(points, params, leftTangent, rightTangent) {
  const first = points[0];
  const last = points[points.length - 1];
  
  // Create the coefficient matrix
  let c00 = 0, c01 = 0, c11 = 0;
  let x0 = 0, x1 = 0;
  
  for (let i = 0; i < points.length; i++) {
    const t = params[i];
    const u = 1 - t;
    const t2 = t * t;
    const u2 = u * u;
    const t3 = t2 * t;
    const u3 = u2 * u;
    
    const a0 = vec2.multiply(leftTangent, 3 * u2 * t);
    const a1 = vec2.multiply(rightTangent, 3 * u * t2);
    
    c00 += vec2.dot(a0, a0);
    c01 += vec2.dot(a0, a1);
    c11 += vec2.dot(a1, a1);
    
    const tmp = vec2.subtract(
      points[i],
      vec2.add(
        vec2.multiply(first, u3),
        vec2.multiply(last, t3)
      )
    );
    
    x0 += vec2.dot(a0, tmp);
    x1 += vec2.dot(a1, tmp);
  }
  
  // Solve the linear system
  const det = c00 * c11 - c01 * c01;
  let alpha0, alpha1;
  
  if (Math.abs(det) > 1e-6) {
    alpha0 = (x0 * c11 - x1 * c01) / det;
    alpha1 = (x1 * c00 - x0 * c01) / det;
  } else {
    // Fallback to simple distance-based estimation
    const distance = vec2.distance(first, last) / 3;
    alpha0 = alpha1 = distance;
  }
  
  // Ensure reasonable handle lengths
  alpha0 = Math.max(0.1, Math.min(alpha0, vec2.distance(first, last)));
  alpha1 = Math.max(0.1, Math.min(alpha1, vec2.distance(first, last)));
  
  const p1 = vec2.add(first, vec2.multiply(leftTangent, alpha0));
  const p2 = vec2.add(last, vec2.multiply(rightTangent, alpha1));
  
  return [first, p1, p2, last];
}

function computeMaxError(points, params, bezier) {
  let maxError = 0;
  let splitPoint = Math.floor(points.length / 2);
  
  for (let i = 1; i < points.length - 1; i++) {
    const t = params[i];
    const bezierPoint = evaluateBezier(bezier, t);
    const error = vec2.distance(points[i], bezierPoint);
    
    if (error > maxError) {
      maxError = error;
      splitPoint = i;
    }
  }
  
  return [maxError, splitPoint];
}

function evaluateBezier(bezier, t) {
  const [p0, p1, p2, p3] = bezier;
  const u = 1 - t;
  const t2 = t * t;
  const u2 = u * u;
  const t3 = t2 * t;
  const u3 = u2 * u;
  
  return {
    x: u3 * p0.x + 3 * u2 * t * p1.x + 3 * u * t2 * p2.x + t3 * p3.x,
    y: u3 * p0.y + 3 * u2 * t * p1.y + 3 * u * t2 * p2.y + t3 * p3.y
  };
}

function fitCubicRecursive(points, leftTangent, rightTangent, maxError, result) {
  if (points.length < 2) return;
  
  if (points.length === 2) {
    // Simple line segment
    const nodeId1 = `node_${Date.now()}_${Math.random()}`;
    const nodeId2 = `node_${Date.now()}_${Math.random()}`;
    
    result.nodes.push({
      id: nodeId1,
      p: points[0],
      t: 'cusp'
    });
    
    result.nodes.push({
      id: nodeId2,
      p: points[1],
      t: 'cusp'
    });
    
    result.segments.push({
      type: 'line',
      from: result.nodes.length - 2,
      to: result.nodes.length - 1
    });
    
    return;
  }
  
  const params = chordLengthParameterize(points);
  const bezier = generateBezier(points, params, leftTangent, rightTangent);
  const [error, splitPoint] = computeMaxError(points, params, bezier);
  
  if (error <= maxError) {
    // Accept this curve
    const startIndex = result.nodes.length;
    const nodeId1 = `node_${Date.now()}_${Math.random()}`;
    const nodeId2 = `node_${Date.now()}_${Math.random()}`;
    
    result.nodes.push({
      id: nodeId1,
      p: bezier[0],
      h2: bezier[1],
      t: 'smooth'
    });
    
    result.nodes.push({
      id: nodeId2,
      p: bezier[3],
      h1: bezier[2],
      t: 'smooth'
    });
    
    result.segments.push({
      type: 'cubic',
      from: startIndex,
      to: startIndex + 1
    });
    
    result.error = Math.max(result.error, error);
  } else {
    // Split and recurse
    const leftPoints = points.slice(0, splitPoint + 1);
    const rightPoints = points.slice(splitPoint);
    
    const centerTangent = computeTangent(points, splitPoint);
    const leftRightTangent = vec2.multiply(centerTangent, -1);
    
    fitCubicRecursive(leftPoints, leftTangent, leftRightTangent, maxError, result);
    fitCubicRecursive(rightPoints, centerTangent, rightTangent, maxError, result);
  }
}

export function fitCubicBeziers(points, maxError = 2.0) {
  const result = {
    nodes: [],
    segments: [],
    error: 0
  };
  
  if (points.length < 2) return result;
  
  const leftTangent = computeTangent(points, 0);
  const rightTangent = computeTangent(points, points.length - 1);
  
  fitCubicRecursive(points, leftTangent, rightTangent, maxError, result);
  
  return result;
}

// ============================================================================
// Main Smoothing Pipeline
// ============================================================================

export function smoothPath(rawPoints, config) {
  if (!config.enabled || rawPoints.length < 2) {
    // Return simple line segments
    const result = { nodes: [], segments: [], error: 0 };
    
    rawPoints.forEach((point, index) => {
      result.nodes.push({
        id: `node_${Date.now()}_${index}`,
        p: point,
        t: 'cusp'
      });
      
      if (index > 0) {
        result.segments.push({
          type: 'line',
          from: index - 1,
          to: index
        });
      }
    });
    
    return result;
  }
  
  // Step 1: Sample points
  const sampled = samplePoints(rawPoints, config.minDistance, config.minTime);
  
  // Step 2: Apply low-pass filter for smoothing
  const filtered = applyLowPassFilter(sampled, 0.3);
  
  // Step 3: Simplify using RDP
  const simplified = rdpSimplify(filtered, config.tolerance);
  
  // Step 4: Fit cubic Bezier curves
  const fitted = fitCubicBeziers(simplified, config.maxError);
  
  return fitted;
}
